import type { AuthConfig, Me } from '@cockpit/shared';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { ApiError, api } from '../api';
import { LOGOUT_PENDING_KEY, useOfflineStore } from './offline';

function logoutPending(): boolean {
  try {
    return localStorage.getItem(LOGOUT_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}
function setLogoutPending(on: boolean): void {
  try {
    if (on) localStorage.setItem(LOGOUT_PENDING_KEY, '1');
    else localStorage.removeItem(LOGOUT_PENDING_KEY);
  } catch {
    // Stockage indisponible : la session du serveur expirera d'elle-même.
  }
}

export const useAuthStore = defineStore('auth', () => {
  const me = ref<Me | null>(null);
  const config = ref<AuthConfig | null>(null);
  const loaded = ref(false);
  let pending: Promise<void> | null = null;

  const isAdmin = computed(() => me.value?.role === 'ADMIN');

  // Lot AW : déconnecté sans réseau : dès que le réseau revient, la session du serveur est fermée (avant toute
  // nouvelle connexion, Google compris).
  watch(
    () => useOfflineStore().online,
    (online) => {
      if (!online || !logoutPending() || me.value) return;
      api
        .logout()
        .then(() => setLogoutPending(false))
        .catch(() => undefined);
    },
  );

  async function fetchMe(): Promise<void> {
    try {
      me.value = await api.me();
    } catch (err) {
      // Lot AW : sans réseau et sans personne gardée sur le téléphone (déconnecté), on montre la connexion.
      if (err instanceof ApiError && (err.status === 401 || err.code === 'OFFLINE_NO_DATA')) me.value = null;
      else throw err;
    }
    // Lot AW : les données gardées sont celles de cette personne (effacées si une autre était connectée).
    if (me.value) await useOfflineStore().setUser(me.value.id);
  }

  /** Charge une fois la configuration de connexion et l'utilisateur courant. */
  function init(): Promise<void> {
    if (loaded.value) return Promise.resolve();
    pending ??= (async () => {
      try {
        // Lot AW : déconnexion faite sans réseau : la session du serveur est fermée dès que le réseau revient.
        if (logoutPending() && useOfflineStore().online) {
          try {
            await api.logout();
            setLogoutPending(false);
          } catch {
            // toujours pas de réseau : on réessaiera
          }
        }
        const [cfg] = await Promise.all([api.authConfig(), logoutPending() ? Promise.resolve() : fetchMe()]);
        config.value = cfg;
        loaded.value = true;
      } finally {
        pending = null;
      }
    })();
    return pending;
  }

  async function devLogin(email: string): Promise<void> {
    me.value = await api.devLogin({ email });
    setLogoutPending(false);
    const offline = useOfflineStore();
    await offline.setUser(me.value.id);
    // Lot AW : la personne connectée est gardée (l'appli s'ouvre sans réseau sur ses données).
    await offline.remember('/api/me', me.value);
  }

  /** Lot AW : tout ce qui est gardé sur le téléphone est effacé, même sans réseau. */
  async function logout(): Promise<void> {
    try {
      await api.logout();
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 0) throw err;
      setLogoutPending(true);
    } finally {
      me.value = null;
      await useOfflineStore().wipe();
    }
  }

  return { me, config, loaded, isAdmin, init, fetchMe, devLogin, logout };
});
